/**
 * Base entity models for user-management-lib
 * 
 * @remarks
 * All domain entities in this library extend from these base interfaces.
 * This ensures consistency across all models and reduces duplication.
 */

/**
 * Base entity interface - all resources extend this
 * 
 * @remarks
 * Every entity in the system has these core fields for tracking
 * creation and modification times, plus a unique identifier.
 */
export interface BaseEntity {
  /**
   * Unique identifier (MongoDB ObjectId as string)
   */
  _id: string;

  /**
   * ISO 8601 timestamp of when the entity was created
   */
  createdAt: string;

  /**
   * ISO 8601 timestamp of when the entity was last updated
   */
  updatedAt: string;
}

/**
 * Entity with soft delete capability
 * 
 * @remarks
 * Entities that extend this interface support soft deletion,
 * meaning they are marked as deleted but not removed from the database.
 */
export interface SoftDeletableEntity extends BaseEntity {
  /**
   * Whether the entity has been soft deleted
   */
  isDeleted: boolean;

  /**
   * ISO 8601 timestamp of when the entity was deleted
   * Only present if isDeleted is true
   */
  deletedAt?: string;
}

/**
 * Entity with active/inactive status
 * 
 * @remarks
 * Entities that extend this interface can be enabled or disabled
 * without deletion, useful for temporarily deactivating resources.
 */
export interface ActivatableEntity extends BaseEntity {
  /**
   * Whether the entity is currently active
   * Inactive entities may be hidden from normal queries
   */
  isActive: boolean;
}
